/**
 * Whether a rich text fragment carries anything a reader would see (ADR 0027).
 *
 * Shared rather than implemented twice, because the two sides have to agree
 * exactly. The composer decides whether Send is enabled; the API decides
 * whether to reject the body. If the client is stricter, Send is disabled on
 * something the server would have taken; if the server is stricter, Send looks
 * available and the post 400s. Both are only visible to whoever hits the one
 * fragment the two rules disagree about.
 *
 * The problem it solves is that every rich editor represents an empty document
 * as `<p></p>`, which is a non-empty STRING and an empty document to a person.
 * A `length > 0` check on the raw HTML therefore treats an editor that was
 * typed into and cleared as a message worth sending.
 *
 * Tag-stripping by regex is deliberate and safe HERE: this is a visibility
 * question, not a sanitizing one. Nothing downstream trusts the result, and
 * the actual security boundary is `sanitizeComposerHtml` on the API, which
 * parses properly. A parser on this path would put cheerio in the widget's
 * dependency tree for a yes/no answer.
 */
export function isEmptyRichText(html: string): boolean {
  // `<br>` and `<img>` are visible without contributing text, so a document
  // consisting only of them is not empty.
  if (/<(br|img)\b/i.test(html)) return false;

  const text = html
    .replace(/<[^>]*>/g, '')
    // Only the entities the escape on the way in can produce, plus nbsp,
    // which is what a browser leaves behind when a space ends a line.
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;|&gt;|&amp;/gi, 'x');

  return text.trim().length === 0;
}
